import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, ChangeDetectionStrategy, ChangeDetectorRef, Renderer2, inject, signal, computed, effect, Signal, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser, NgOptimizedImage, DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { identity } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { selectSidebarMode, selectSidebarHidden } from 'src/app/stores/sidebar/sidebar.selectors';
import { toggleSidebar, hideSidebar, restoreSidebarVisibility } from 'src/app/stores/sidebar/sidebar.actions';
import { LayoutService } from 'src/app/stores/layout/layout.service';
import { BreakpointObserver } from '@angular/cdk/layout';

import { allAccounts, allPortfolioLoading } from 'src/app/stores/portfolio/portfolio.selectors';
import { PortfolioActions } from 'src/app/stores/portfolio/portfolio.actions';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FeaturebaseService } from 'src/app/shared/services/featurebase.service';
import { PortfolioAccount } from 'src/app/features/our-portfolio/models/account-list-response.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CountryService } from 'src/app/shared/services/country.service';

import { MenuItem, getSidebarMenuItems, getSidebarFooterItems } from '../header-links';

@Component({
   selector: 'app-sidebar-menu',
   templateUrl: './sidebar-menu.component.html',
   styleUrls: ['./sidebar-menu.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   standalone: true,
   imports: [
      CommonModule,
      RouterModule,
      NgbTooltipModule,
      NgbCollapseModule,
      NgxSkeletonLoaderModule,
      NgOptimizedImage
   ]
})
export class SidebarMenuComponent implements OnDestroy {
   private readonly store = inject(Store);
   private readonly layoutService = inject(LayoutService);
   public readonly featurebaseService = inject(FeaturebaseService);
   private readonly authService = inject(AuthService);
   private readonly breakpointObserver = inject(BreakpointObserver);
   private readonly router = inject(Router);
   private readonly route = inject(ActivatedRoute);
   private readonly countryService = inject(CountryService);
   private readonly renderer = inject(Renderer2);
   private readonly platformId = inject(PLATFORM_ID);
   private readonly document = inject(DOCUMENT);
   private readonly isBrowser = isPlatformBrowser(this.platformId);

   private readonly MOBILE_BREAKPOINT = '(max-width: 1199px)';

   readonly mode = toSignal(this.store.select(selectSidebarMode), { initialValue: 'expanded' as 'expanded' | 'collapsed' | 'hidden' });
   readonly isHidden = toSignal(this.store.select(selectSidebarHidden), { initialValue: false });
   
   readonly accounts = toSignal(
      this.store.select(allAccounts).pipe(this.isBrowser ? delay(0) : identity),
      { initialValue: [] as PortfolioAccount[] }
   );
   readonly loading = toSignal(this.store.select(allPortfolioLoading).pipe(this.isBrowser ? delay(0) : identity), { initialValue: false });

   readonly isMobile = toSignal(
      this.breakpointObserver.observe('(max-width: 1199px)').pipe(map(res => res.matches)),
      { initialValue: false }
   );
   readonly activeAccountId = toSignal(this.route.queryParams.pipe(map(params => params['accountId'])));
   readonly selectedCountry = toSignal(this.countryService.selectedCountry$.pipe(map(c => c.countryCode)), { initialValue: '' });
   readonly isUserLoggedIn = computed(() => this.isBrowser && !!this.authService.getUserId());
   
   readonly activePath = toSignal(
      this.router.events.pipe(
         filter((e): e is NavigationEnd => e instanceof NavigationEnd),
         map(e => e.urlAfterRedirects.split('?')[0])
      ),
      { initialValue: this.router.url.split('?')[0] }
   );

   readonly collapsedStates = signal<Record<string, boolean>>({
      'home-mobile': true,
      'learn': true,
      'for-business': true,
      'more': true
   });

   readonly labelsVisible = computed(() => this.mode() === 'expanded');

   readonly menuItems = computed<MenuItem[]>(() => {
      const isMobile = this.isMobile();
      const accounts = this.accounts();
      const portfolioItem: MenuItem = this.getPortfolioMenuItem(accounts);
      const items = getSidebarMenuItems(isMobile, portfolioItem);

      // Inject dynamic route for Halal Collections to support SEO-friendly URLs and middle-clicks
      const country = (this.selectedCountry() || 'US').toLowerCase();
      const researchSection = items.find(i => i.label === 'Research');
      if (researchSection && researchSection.children) {
         const collectionsItem = researchSection.children.find(c => c.id === 'halal-collections');
         if (collectionsItem) {
            collectionsItem.route = `/halal-collection/${country}/technology-stocks`;
         }
      }

      return items;
   });

   constructor() {
      // Body scroll lock effect
      effect(() => {
         this.updateBodyScrollLock();
      });

      // Auto-collapse on navigation (using standard subscription to avoid NG0600 reactive context conflict)
      this.router.events.pipe(
         filter(e => e instanceof NavigationEnd),
         takeUntilDestroyed()
      ).subscribe(() => {
         untracked(() => {
            if (this.isMobile() && this.mode() === 'expanded') {
               this.store.dispatch(hideSidebar());
            }
         });
      });
      
      // Initial data load
      if (this.isBrowser) {
         untracked(() => {
            this.store.dispatch(PortfolioActions.loadAccounts());
         });
      }
   }

   // --- Actions & Handlers ---
   toggle(): void {
      if (this.isMobile()) return;
      this.store.dispatch(toggleSidebar());
   }

   closeMobile(): void {
      this.store.dispatch(hideSidebar());
   }

   openMobile(): void {
      this.store.dispatch(restoreSidebarVisibility());
   }

   ngOnDestroy(): void {
      if (this.isBrowser) {
         this.renderer.removeStyle(this.document.body, 'overflow');
      }
   }

   openLoginOrNavigate(path: string | undefined, queryParams?: any, isGuarded?: boolean): void {
      if (!this.isBrowser || !path) return;

      const isLoggedIn = this.isUserLoggedIn();
      
      if (isLoggedIn || !isGuarded) {
         this.router.navigate([path], { queryParams });
         if (this.isMobile()) this.store.dispatch(hideSidebar());
      } else {
         this.authService.setSignupSource('Side Nav');
         this.authService.openAuthPopup();
         // No longer closing sidebar here, per user request
      }
   }

   openLoginPopup(): void {
      this.authService.setSignupSource('Side Nav');
      this.authService.openAuthPopup();
   }

   toggleCollapse(id: string): void {
      this.collapsedStates.update(states => ({
         ...states,
         [id]: !states[id]
      }));
   }


   isCollapsed(id: string): boolean {
      return this.collapsedStates()[id] ?? true;
   }
   
   isLinkActive(item: MenuItem): boolean {
      const activePath = this.activePath();
      const currentAccountId = this.activeAccountId();
      
      // Basic route match
      let pathMatches = activePath === item.route;

      // Special case for Halal Stock Collections (handles dynamic SEO paths)
      if (item.id === 'halal-collections') {
         pathMatches = activePath.startsWith('/halal-collection');
      }

      if (!pathMatches) return false;
      
      // If queryParams are present, check accountId specifically
      if (item.queryParams && item.queryParams['accountId']) {
         return item.queryParams['accountId'] === currentAccountId;
      }
      
      return true;
   }

   private getPortfolioMenuItem(accounts: PortfolioAccount[]): MenuItem {
      if (this.loading()) {
         return { label: 'Loading...', type: 'dynamic-portfolio', id: 'portfolio-loading' };
      }

      const isLoggedIn = this.isUserLoggedIn();
      const realAccount = accounts?.find(acc => !acc.is_sample);
      const targetAccountId = realAccount ? realAccount.id : 'sample';

      return {
         label: 'My Linked Portfolio',
         route: '/portfolio-dashboard',
         queryParams: { accountId: targetAccountId },
         icon: 'portfolio',
         isPublic: targetAccountId === 'sample',
         isGuarded: targetAccountId !== 'sample'
      };
   }

   private updateBodyScrollLock(): void {
      if (!this.isBrowser) return;

      const shouldLock = this.isMobile() && this.mode() === 'expanded';
      
      if (shouldLock) {
         this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
         this.renderer.addClass(this.document.body, 'sidebar-open');
      } else {
         this.renderer.removeStyle(this.document.body, 'overflow');
         this.renderer.removeClass(this.document.body, 'modal-open');
         this.renderer.removeClass(this.document.body, 'sidebar-open');
      }
   }

   onPortfolioClick(accountId: string): void {
      if (this.isMobile()) {
         this.store.dispatch(hideSidebar());
      }
   }
   
   onSidebarTransitionEnd(event: TransitionEvent): void {
      if (!this.isBrowser) return;
      if (event.propertyName !== 'width') return;
      // Derived labelsVisible will handle the change automatically
   }

}
